import { Link } from "react-router-dom";
import { useState } from "react";
import { Compass, Star, Users, Globe } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadModal from "@/components/LeadModal";

const SERVICES = [
  {
    icon: Compass,
    title: "Custom Travel Design",
    tagline: "Personalized journeys. Designed around you.",
    desc: "Personalized journeys crafted around your identity and preferences. We listen to who you are, understand what you seek, and build every detail around your Soulprint — not a template.",
    points: ["Bespoke itinerary creation", "Soulprint-matched experiences", "Personal Experience Architect assigned", "24/7 journey support"],
    image: "https://images.unsplash.com/photo-1501854140801-50d01698950b?w=800&q=80",
    archetype: "For every Soulprint"
  },
  {
    icon: Star,
    title: "Luxury Curated Experiences",
    tagline: "Where no guidebook will take you.",
    desc: "Handpicked, high-end experiences designed for exclusivity and depth. Private access, flawless curation, and nothing left to chance — every detail chosen because it belongs to your story, not someone else's.",
    points: ["Exclusive private access", "Luxury accommodation selection", "Personal sommelier & chef arrangements", "Seamless ground coordination"],
    image: "https://images.unsplash.com/photo-1559329007-40df8a9345d8?w=800&q=80",
    archetype: "For the Sensorialist & Romantic"
  },
  {
    icon: Users,
    title: "Group & Experiential Travel",
    tagline: "When the right people gather in the right place.",
    desc: "Immersive journeys for like-minded travelers and curated groups. Retreats, celebrations, adventures — designed for those who travel to feel something together, and return changed.",
    points: ["Corporate retreats & team experiences", "Wellness & transformation groups", "Celebration journeys (birthdays, milestones)", "Music & cultural immersion groups"],
    image: "https://images.pexels.com/photos/4218027/pexels-photo-4218027.jpeg?auto=compress&cs=tinysrgb&w=800",
    archetype: "For the Celebrator & Soul Traveler"
  },
  {
    icon: Globe,
    title: "International Signature Journeys",
    tagline: "Perfected over years of obsessive curation.",
    desc: "Our flagship multi-destination experiences, refined through years of deep exploration. These are not tours — they are choreographed transformations, from the first arrival to the last farewell.",
    points: ["Multi-country signature routes", "Pre-curated seasonal experiences", "Local immersion & cultural access", "Visa & logistics fully managed"],
    image: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&q=80",
    archetype: "For the Explorer & Seeker"
  },
  {
    icon: Users,
    title: "MICE & Corporate Travel",
    tagline: "Where business meets transformation.",
    desc: "Strategically designed travel experiences for organizations, events, and corporate engagements. From intimate leadership retreats to large-scale incentive programmes — every detail serves a purpose beyond the boardroom.",
    points: ["Incentive trip design & execution", "Corporate retreat planning", "Conference & MICE logistics", "Team transformation experiences", "Venue sourcing & full coordination"],
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80",
    archetype: "For Organisations & Teams"
  },
  {
    icon: Compass,
    title: "Private Chartered Flights",
    tagline: "The journey begins in the air.",
    desc: "Seamless, premium travel with complete flexibility and privacy. The moment the cabin door closes and the world falls away, the experience has already begun. We arrange charters that set the tone for everything that follows.",
    points: ["Private jet charter (domestic & international)", "Helicopter transfers to remote destinations", "Business jet for group travel", "Last-minute & emergency arrangements", "Full flight operations support"],
    image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&q=80",
    archetype: "For Any Journey, Elevated"
  }
];

export default function Services() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="services-page">
      <Navbar onPlanJourney={() => setModalOpen(true)} />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center text-center px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#0A0A0A]" />
        </div>
        <div className="relative z-10 max-w-3xl pt-24">
          <p className="overline mb-6">What We Offer</p>
          <h1 className="font-heading text-5xl md:text-7xl font-light text-white tracking-tight leading-none mb-6">
            Beyond Travel.
            <br />
            <span className="italic text-[#C9A84C]">Experience Architecture.</span>
          </h1>
          <p className="text-[#A1A1AA] text-base max-w-xl mx-auto leading-relaxed">
            A unified approach to designing journeys that transform the people who take them.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto space-y-20">
          {SERVICES.map((svc, i) => (
            <div
              key={svc.title}
              data-testid={`service-card-${i}`}
              className={`grid md:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`}
            >
              <div className={`${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="relative h-72 md:h-96 rounded-xl overflow-hidden card-zoom">
                  <img src={svc.image} alt={svc.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] border border-[#C9A84C]/30 px-3 py-1 rounded-sm bg-black/50">
                      {svc.archetype}
                    </span>
                  </div>
                </div>
              </div>

              <div className={`${i % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="flex items-center gap-3 mb-4">
                  <svc.icon size={18} className="text-[#C9A84C]" />
                  <p className="overline text-[#C9A84C]">{svc.tagline}</p>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-5 tracking-tight">{svc.title}</h2>
                <p className="text-[#A1A1AA] text-base leading-relaxed mb-6">{svc.desc}</p>
                <ul className="space-y-2 mb-8">
                  {svc.points.map(pt => (
                    <li key={pt} className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                      <span className="w-1 h-1 rounded-full bg-[#C9A84C] shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`service-cta-${i}`}
                  onClick={() => setModalOpen(true)}
                  className="btn-gold text-xs px-8 py-3"
                >
                  Design My Journey
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#111111]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="overline mb-4">How It Works</p>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-white mb-14 tracking-tight">
            From First Conversation to
            <span className="italic text-[#C9A84C]"> Transformation</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Discover Your Soulprint", desc: "Take our quiz or speak with Aura to uncover your travel archetype and transformation desires." },
              { step: "02", title: "Meet Your Architect", desc: "A dedicated Experience Architect is assigned to you — they'll learn everything about what you seek." },
              { step: "03", title: "Journey Unfolds", desc: "Your bespoke experience is crafted, refined, and executed — while you simply arrive and be transformed." }
            ].map(step => (
              <div key={step.step} data-testid={`process-step-${step.step}`} className="text-left">
                <p className="font-heading text-5xl font-light text-[#C9A84C]/30 mb-3">{step.step}</p>
                <h3 className="font-heading text-xl font-light text-white mb-3">{step.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <button
            data-testid="services-cta-btn"
            onClick={() => setModalOpen(true)}
            className="btn-gold text-xs px-12 py-4 mt-14"
          >
            Design My Journey
          </button>
        </div>
      </section>

      <Footer onPlanJourney={() => setModalOpen(true)} />
      <LeadModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
